import {
    isOrderStreamUpdatedRequiringFilterRecalculation,
    OrdersActionType,
    ordersFilterResultsChanged,
} from 'app/store/orders/actions';
import { OrderStreamUpdatedEvent } from 'app/store/orders/orderStreamsUpdatedEvent';
import { AnyAction, MiddlewareAPI } from 'redux';
import { selectActiveInput } from 'portfolio/store/selectors';
import { WidgetType } from 'widgets/WidgetType';
import State from 'store/State';
import { buildFiltersFunction } from 'utils/filter/functions/filtersFunction';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { selectBlotterConfiguration } from '../configuration/selectors';
import { selectAppCurrency } from 'app/store/selectors';
import {
    OrderFilterResultsChangedEventBuilder,
    OrderFilterResultsChangedEventType,
} from 'app/store/orders/orderFilterResultsChangedEvent';
import { incrementResultsTick } from './actions';
import { selectBlotterFilteredOrdersResults } from './selectors';
import { selectOrdersState } from 'app/store/orders/selectors';
import { PortfolioActionType } from 'portfolio/store/actions';
import { ThunkDispatch } from 'store/store';
import { CurrencyActionType } from 'app/currencies/store/actions';
import { isAppSettingsSymbologyChange } from 'app/store/appSettings/actions';
import { buildLogger } from '@instinet-analytics/core-lib';
import Services from 'store/Services';
import {
    buildColumnDefinitions,
    buildColumnPortfolio,
} from 'widgets/blotter/columns/ColumnPortfolio';
import { WidgetActionType } from 'widgets/store/actions';
import { WidgetSetConfigAction } from 'widgets/store/configuration/actions';
import { BlotterConfiguration } from 'widgets/blotter/store/configuration/BlotterConfiguration';
import { using as usingStatement } from 'using-statement';

export const blotterFilteredOrdersMiddleware = (services: Services) => [
    resetFilterResultsMiddleware(services),
    updateActiveBlotterFilterResultsOnOrdersStreamUpdatedMiddleware(services),
];

export function updateActiveBlotterFilterResultsOnOrdersStreamUpdatedMiddleware(
    services: Services
) {
    const { actionsDeferredDispatcherFactory } = services;

    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            // update the state
            next(action);

            if (action.type !== OrdersActionType.ORDERS_STREAM_UPDATED) {
                return;
            }

            const orderStreamUpdatedEvent =
                action.payload! as OrderStreamUpdatedEvent;

            if (isOrderStreamUpdatedRequiringFilterRecalculation(action)) {
                // Will be handled by resetFilterResultsMiddleware
                return;
            }

            const state = middlewareAPI.getState();

            // We only recalculate the filters if the active input is a blotter...
            const activeBlotterWidgetKey = getActiveBlotterWidgetKey(state);
            if (!activeBlotterWidgetKey) {
                return;
            }

            const filteredOrders = selectBlotterFilteredOrdersResults(
                state,
                activeBlotterWidgetKey
            );

            const { symbology } = selectAppSettings(state);
            const columnPortfolio = buildColumnPortfolio(symbology);
            const { filters } = selectBlotterConfiguration(
                state,
                activeBlotterWidgetKey,
                columnPortfolio
            );
            const currency = selectAppCurrency(state);

            const columnDefinitions = buildColumnDefinitions(columnPortfolio);

            const filterFn = buildFiltersFunction(
                columnDefinitions,
                filters,
                currency?.rate ?? 1
            );

            const filterChangeEventBuilder =
                new OrderFilterResultsChangedEventBuilder(
                    orderStreamUpdatedEvent.batchUpdateInProgress
                );

            for (let i = 0; i < orderStreamUpdatedEvent.added.length; i++) {
                const added = orderStreamUpdatedEvent.added[i];
                if (added) {
                    const passes = filterFn(added);
                    filteredOrders[i] = { idx: i, passes };
                    if (passes) {
                        // Newly added order passes filter so record this as a new member...
                        filterChangeEventBuilder.newMember(i, { next: added });
                    }
                }
            }

            for (let i = 0; i < orderStreamUpdatedEvent.updated.length; i++) {
                const updated = orderStreamUpdatedEvent.updated[i];
                const didPass = filteredOrders[i]?.passes;
                if (updated) {
                    const passes = filterFn(updated.next);

                    filteredOrders[i] = {
                        idx: i,
                        passes: filterFn(updated.next),
                    };

                    if (passes) {
                        if (didPass) {
                            // Updated order still passes, so record as an update...
                            filterChangeEventBuilder.updatedMember(i, updated);
                        } else {
                            // Updates order now passes, so record as a new member...
                            filterChangeEventBuilder.newMember(i, updated);
                        }
                    } else {
                        if (didPass) {
                            // Updated order no longer passes filter, so record as a removal...
                            filterChangeEventBuilder.removedMember(i, updated);
                        }
                    }
                }
            }

            // TODO: removals when we tackle IND2-308

            if (filterChangeEventBuilder.haveChanges) {
                usingStatement(
                    actionsDeferredDispatcherFactory.new(),
                    (dispatcher) => {
                        // Dispatch the changed action for middleware...
                        dispatcher.dispatch(
                            ordersFilterResultsChanged(
                                filterChangeEventBuilder.event
                            )
                        );
                        // Increment the tick for the useBlotterFilteredOrders hook...
                        dispatcher.dispatch(
                            incrementResultsTick(activeBlotterWidgetKey)
                        );
                    }
                );
            }
        };
}

/*
 * Reset the filter results when blotter config is changed, the active input changes or relevant app settings changed
 */
export function resetFilterResultsMiddleware(services: Services) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const { loggingService, actionsDeferredDispatcherFactory } =
                services;

            const logger = buildLogger(
                'filteredOrders resetFilterResultsMiddleware',
                loggingService
            );

            const prevState = middlewareAPI.getState();

            // update the state
            next(action);

            const state = middlewareAPI.getState();

            if (
                !actionMayRequireFilterRecalculation(action, prevState, state)
            ) {
                return;
            }

            // We only recalculate the results if the active input is a blotter...
            const activeBlotterWidgetKey = getActiveBlotterWidgetKey(state);
            if (!activeBlotterWidgetKey) {
                return;
            }

            const filteredOrders = selectBlotterFilteredOrdersResults(
                state,
                activeBlotterWidgetKey
            );

            const { symbology } = selectAppSettings(state);
            const columnPortfolio = buildColumnPortfolio(symbology);
            const { filters } = selectBlotterConfiguration(
                state,
                activeBlotterWidgetKey,
                columnPortfolio
            );
            const currency = selectAppCurrency(state);

            const columnDefinitions = buildColumnDefinitions(columnPortfolio);

            const filterFn = buildFiltersFunction(
                columnDefinitions,
                filters,
                currency?.rate ?? 1
            );

            const { data: orders } = selectOrdersState(state);
            if (!orders) {
                return;
            }

            for (let i = 0; i < orders.length; i++) {
                const order = orders[i];
                if (order) {
                    const passes = filterFn(order);
                    filteredOrders[i] = { idx: i, passes };
                } else {
                    if (filteredOrders[i]) {
                        filteredOrders[i] = undefined;
                    }
                }
            }

            logger.info('Resetting filter results', action.type);

            usingStatement(
                actionsDeferredDispatcherFactory.new(),
                (dispatcher) => {
                    dispatcher.dispatch(
                        ordersFilterResultsChanged({
                            changedType:
                                OrderFilterResultsChangedEventType.RESET,
                        })
                    );
                    dispatcher.dispatch(
                        incrementResultsTick(activeBlotterWidgetKey)
                    );
                }
            );
        };
}

function getActiveBlotterWidgetKey(state: State): string | undefined {
    const activeInput = selectActiveInput(state);
    if (activeInput.activeInputType !== 'Widget') {
        return undefined;
    }
    if (activeInput.activeInputWidget?.type !== WidgetType.BLOTTER) {
        return undefined;
    }
    return activeInput.activeInputWidget!.id;
}

function actionMayRequireFilterRecalculation(
    action: AnyAction,
    prevState: State,
    nextState: State
): boolean {
    return (
        ((action as WidgetSetConfigAction<BlotterConfiguration>).payload
            ?.widgetType === WidgetType.BLOTTER &&
            action.type === WidgetActionType.WIDGET_SET_CONFIG) ||
        action.type === PortfolioActionType.PORTFOLIO_SET_ACTIVE_INPUT ||
        action.type === CurrencyActionType.SET_CURRENCIES ||
        isAppSettingsSymbologyChange(prevState, nextState) ||
        isOrderStreamUpdatedRequiringFilterRecalculation(action)
    );
}
